import React from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { TableCell, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import useModalStore from "@/store/useModalStore";
import CustomSheet from "@/components/shared/Sheet/Sheet";
import {
  Edit,
  ExternalLink,
  Plus,
  Link as LinkIcon,
  Trash,
  Copy,
  Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Badge from "@/components/shared/Badge/Badge";
import { CreateLink } from "./Partials/CreateLink";
import { EditLink } from "./Partials/EditLink";
import { LinksService } from "@/services/links.service";
import { ProductDetailsResponse, ProductRole } from "@/@types/Products";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import CTATable from "@/components/shared/TableListCustom/CTATable";
import { Link } from "@/@types/Links";
import DialogDelete from "@/components/shared/DialogDelete/DialogDelete";
import { useMutation } from "@tanstack/react-query";
import { copyToClipboard } from "@/utils/copyToClipboard";
import { usePermissions } from "@/auth/PermissionsContext";

export default function Links() {
  const { modals, openModal, closeModal } = useModalStore();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["links", "common"]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [link, setLink] = React.useState<Link>();
  const [copiedLinkId, setCopiedLinkId] = React.useState<string | null>(null);
  const { product } = useOutletContext<{ product: ProductDetailsResponse }>();
  const queryClient = useQueryClient();

  const { hasPermission } = usePermissions();
  const isProducer = product?.relationship?.role === ProductRole.PRODUCER;
  const canCreateLink = isProducer && hasPermission('links.create_link');
  const canEditLink = isProducer && hasPermission('links.edit_link');
  const canDeleteLink = isProducer && hasPermission('links.delete_link');

  const { data: links, isPending: isLoadingLinks } = useQuery({
    queryKey: [`links-${id!}`, currentPage],
    queryFn: async () =>
      product?.relationship?.role === ProductRole?.AFFILIATE
        ? await LinksService.getLinksAsAffiliates({
            productId: id,
            page: currentPage,
          })
        : await LinksService.getLinks({ productId: id, page: currentPage }),
  });

  const headers = [
    {
      title: t("common:name_single"),
      key: "name",
    },
    {
      title: t("common:link"),
      key: "redirect_url",
    },
    {
      title: t("available_for_affiliates"),
      key: "available_affiliates",
    },
    {
      title: "",
      key: "CTA",
    },
  ];

  const { mutateAsync: deleteLink, isPending: isDeletingLink } = useMutation({
    mutationFn: async () => {
      if (!link?.id || !id) return;
      await LinksService.deleteLink({
        linkId: link.id,
        productId: id,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [`links-${id}`] });

      const previousLinks = queryClient.getQueriesData({
        queryKey: [`links-${id}`],
      });

      return { previousLinks };
    },
    onError: (error, variables, context) => {
      if (context?.previousLinks) {
        context.previousLinks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      queryClient.setQueriesData(
        { queryKey: [`links-${id}`], exact: false },
        (oldData: any) => {
          if (!oldData || !link?.id) return oldData;

          return {
            ...oldData,
            data: oldData.data.filter((l: Link) => l.id !== link.id),
            meta: {
              ...oldData.meta,
              total: oldData.meta.total - 1,
            },
          };
        }
      );
      closeModal("delete-link");
    },
  });

  function handleCopyToClipboard(url: string, linkId: string) {
    const result = copyToClipboard(url);
    if (result) {
      setCopiedLinkId(linkId);
      setTimeout(() => {
        setCopiedLinkId(null);
      }, 2000);
    } else {
      setCopiedLinkId(null);
    }
  }

  return (
    <>
      {canCreateLink && (
        <CustomSheet
          open={modals["create-link"]}
          onClose={() => closeModal("create-link")}
        >
          <CreateLink />
        </CustomSheet>
      )}
      {canEditLink && (
        <CustomSheet
          open={modals["edit-link"]}
          onClose={() => closeModal("edit-link")}
        >
          <EditLink link={link} />
        </CustomSheet>
      )}
      {canDeleteLink && (
        <DialogDelete
          onClose={() => closeModal("delete-link")}
          isOpen={modals["delete-link"]}
          title={t("delete_link_title")}
          description={t("delete_link_description")}
          onDelete={async () => {
            await deleteLink();
          }}
          isDeleting={isDeletingLink}
        />
      )}

      <Card className="w-full mt-8 bg-[#09090b] overflow-x-auto">
        <div className="flex md:items-center gap-3 flex-col md:flex-row w-full p-4 justify-between">
          <h1 className="text-base font-semibold">{t("links")}</h1>
          {canCreateLink && (
            <Button
              data-cy="btn-create-link"
              className="w-full md:max-w-[155px]"
              onClick={() => openModal("create-link")}
            >
              <Plus />
              {t("create_link")}
            </Button>
          )}
        </div>
        <TableListCustom
          emptyTitleMessage={t("emptyLinks.title")}
          emptyDescriptionMessage={t("emptyLinks.description")}
          emptyIconMessage={<LinkIcon />}
          headers={headers}
          isLoadingData={isLoadingLinks}
          data={links}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        >
          {links?.data?.map((link) => {
            const linkToCopy = link.origin_url;
            return (
              <TableRow
                key={link.id}
                className="h-[64px] cursor-normal border-b border-b-colorBorder hover:bg-transparent"
                data-cy="link-table-row"
              >
                <TableCell className="font-medium">{link.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="inline-block cursor-pointer w-auto">
                      <a
                        href={linkToCopy}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#27272a] flex items-center gap-4 w-auto rounded-md p-1"
                      >
                        <span className="truncate flex-1">{linkToCopy}</span>
                        <ExternalLink
                          className="cursor-pointer text-muted flex-shrink-0"
                          width={18}
                          height={18}
                        />
                      </a>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 h-8 w-8"
                      onClick={() => handleCopyToClipboard(linkToCopy, link.id)}
                    >
                      {copiedLinkId === link.id ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    status={link.available_affiliates ? "active" : "inactive"}
                  />
                </TableCell>
                <TableCell>
                  {(canEditLink || canDeleteLink) && (
                    <CTATable
                      items={[
                        ...(canEditLink ? [{
                          id: 1,
                          label: t("edit"),
                          icon: <Edit />,
                          action: () => {
                            setLink(link);
                            openModal("edit-link");
                          },
                        }] : []),
                        ...(canDeleteLink ? [{
                          id: 2,
                          label: t("delete"),
                          icon: <Trash color="#F87171" />,
                          action: () => {
                            setLink(link);
                            openModal("delete-link");
                          },
                        }] : []),
                      ]}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableListCustom>
      </Card>
    </>
  );
}
